import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckIcon,
  ClipboardTextIcon,
  WarningIcon,
  XIcon,
} from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { toast } from 'sonner';
import type { HarnessActionApplyResult } from '@erp/rpc';
import type { HarnessActionPendingResult } from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import { invalidateOrpcPaths } from '@/api/rpc/invalidateByPath';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { NamiActionThumbs } from './NamiActionThumbs';
import { NamiChangeList } from './namiChangeList';
import { NamiViaBadge } from './NamiViaBadge';
import {
  appliedOutcome,
  derivePreviewCardStatus,
  type PreviewCardStatus,
  type PreviewDecision,
} from './previewCardStatus';

type Preview = HarnessActionPendingResult['preview'];

function PreviewTable({ table }: { table: NonNullable<Preview['table']> }) {
  return (
    <div className="max-h-64 overflow-auto rounded border">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-muted/60 text-left">
          <tr>
            {table.columns.map((column) => (
              <th key={column} className="px-2 py-1 font-medium">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-t">
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={
                    typeof cell === 'number'
                      ? 'px-2 py-1 text-right tabular-nums'
                      : 'px-2 py-1'
                  }
                >
                  {cell === null ? '' : String(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PreviewWarnings({ warnings }: { warnings: Preview['warnings'] }) {
  if (!warnings || warnings.length === 0) return null;
  return (
    <ul className="space-y-0.5">
      {warnings.map((warning) => (
        <li
          key={warning}
          className="flex items-start gap-1 text-xs text-amber-700"
        >
          <WarningIcon className="mt-0.5 size-3 shrink-0" />
          {warning}
        </li>
      ))}
    </ul>
  );
}

function PreviewFacts({ facts }: { facts: Preview['facts'] }) {
  if (!facts || facts.length === 0) return null;
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
      {facts.map((fact) => (
        <div key={fact.label} className="contents">
          <dt className="text-muted-foreground">{fact.label}</dt>
          <dd>{fact.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** What applying did (or why it failed), once this card applied. */
function PreviewOutcome({
  outcome,
}: {
  outcome: HarnessActionApplyResult | null;
}) {
  if (!outcome) return null;
  const applied = outcome.status === 'applied';
  return (
    <div className="space-y-1 rounded border bg-background p-2 text-xs">
      <div className={applied ? 'text-foreground' : 'text-destructive'}>
        {applied ? outcome.summary : (outcome.error ?? outcome.summary)}
      </div>
      {outcome.changes ? <NamiChangeList changes={outcome.changes} /> : null}
    </div>
  );
}

function PreviewStatusLabel({ status }: { status: PreviewCardStatus }) {
  let label;
  switch (status) {
    case 'applying':
      label = <T context="AI assistant preview card status">Applying…</T>;
      break;
    case 'applied':
      label = <T context="AI assistant preview card status">Applied</T>;
      break;
    case 'failed':
      label = <T context="AI assistant preview card status">Failed</T>;
      break;
    case 'discarded':
      label = <T context="AI assistant preview card status">Discarded</T>;
      break;
    default:
      label = <T context="AI assistant preview card status">Expired</T>;
  }
  return (
    <span className="text-xs text-muted-foreground" data-status={status}>
      {label}
    </span>
  );
}

function PreviewActions({
  busy,
  onApply,
  onDiscard,
}: {
  busy: boolean;
  onApply: () => void;
  onDiscard: () => void;
}) {
  return (
    <>
      <Button size="sm" disabled={busy} onClick={onApply}>
        <CheckIcon className="size-4" />
        <T context="apply button on an AI assistant preview card">Apply</T>
      </Button>
      <Button size="sm" variant="ghost" disabled={busy} onClick={onDiscard}>
        <XIcon className="size-4" />
        <T context="discard button on an AI assistant preview card">Discard</T>
      </Button>
    </>
  );
}

/**
 * A preview the assistant produced for a bulk or irreversible action. Truth
 * lives server-side (`pendingActionId`): Apply and Discard call the harness
 * procedures, and a card rendered from history asks the server for its state.
 */
export function NamiPreviewCard({
  result,
}: {
  result: HarnessActionPendingResult;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const [mountedAt] = useState(() => Date.now());
  const [decision, setDecision] = useState<PreviewDecision>(null);

  const row = useQuery({
    ...orpc.harness.actions.get.queryOptions({
      input: { pendingActionId: result.pendingActionId },
    }),
    // Only worth asking when this card may be stale (history replay).
    enabled: decision === null,
    staleTime: 15_000,
  });

  const apply = useMutation({
    ...orpc.harness.actions.apply.mutationOptions(),
    onSuccess: (outcome) => {
      setDecision(outcome);
      invalidateOrpcPaths(queryClient, outcome.invalidates);
      if (outcome.status === 'applied') toast.success(outcome.summary);
      else toast.error(outcome.error ?? outcome.summary);
    },
    onError: (error) =>
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Could not apply the preview', {
              context: 'apply failure toast',
            }),
      ),
  });
  const discard = useMutation({
    ...orpc.harness.actions.discard.mutationOptions(),
    onSuccess: () => setDecision('discarded'),
    onError: (error) =>
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Could not discard the preview', {
              context: 'discard failure toast',
            }),
      ),
  });

  const status = derivePreviewCardStatus({
    decision,
    applyPending: apply.isPending,
    rowStatus: row.data?.status,
    expiresAt: result.expiresAt,
    mountedAt,
  });
  const { preview } = result;

  return (
    <Card className="flex flex-col gap-2 border-agent-border bg-agent-subtle p-3">
      <div className="flex items-start gap-2">
        <ClipboardTextIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="text-sm font-medium text-card-foreground">
            {preview.title}
          </div>
          <div className="text-xs text-muted-foreground">{preview.summary}</div>
          <NamiViaBadge />
        </div>
      </div>
      <PreviewWarnings warnings={preview.warnings} />
      {preview.table ? <PreviewTable table={preview.table} /> : null}
      <PreviewFacts facts={preview.facts} />
      <PreviewOutcome outcome={appliedOutcome(decision)} />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {status === 'pending' ? (
            <PreviewActions
              busy={apply.isPending || discard.isPending}
              onApply={() =>
                apply.mutate({ pendingActionId: result.pendingActionId })
              }
              onDiscard={() =>
                discard.mutate({ pendingActionId: result.pendingActionId })
              }
            />
          ) : (
            <PreviewStatusLabel status={status} />
          )}
        </div>
        <NamiActionThumbs toolCallId={result.toolCallId} />
      </div>
    </Card>
  );
}
